import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "../cn";
import { IconTile } from "./IconTile";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Lifts on hover (Level 2 elevation) — for clickable cards. */
  interactive?: boolean;
  padding?: "none" | "sm" | "md" | "lg";
  as?: "div" | "section" | "article" | "aside";
}

const paddings = { none: "", sm: "p-4", md: "p-5", lg: "p-5 md:p-6" };

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { interactive, padding = "md", as: Tag = "div", className, ...props },
  ref,
) {
  return (
    <Tag
      ref={ref}
      className={cn(
        "relative rounded-card border border-white/70 bg-surface-container-lowest shadow-elevated",
        interactive && "transition-[box-shadow,transform] duration-200 ease-out-soft hover:-translate-y-0.5 hover:shadow-elevated-hover",
        paddings[padding],
        className,
      )}
      {...props}
    />
  );
});

export interface SectionHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

/** Card header: tinted icon tile + title + optional action on the right. */
export function SectionHeader({ title, description, icon, action, className }: SectionHeaderProps) {
  return (
    <div className={cn("flex items-start justify-between gap-3", className)}>
      <div className="flex min-w-0 items-center gap-3">
        {icon && <IconTile size="sm">{icon}</IconTile>}
        <div className="min-w-0">
          <h2 className="text-headline-sm text-on-surface">{title}</h2>
          {description && <p className="mt-0.5 text-body-sm text-on-surface-variant">{description}</p>}
        </div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
